import { createContext, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { authClient } from "@/lib/auth-client";
import { ROUTES } from "@/lib/routes";
import { trackFormSubmission, trackUserAction } from "@/lib/sentryBreadcrumbs";
import { validateEmailDomainDNS } from "@/lib/validation";
import { sessionManager } from "@/lib/sessionManager";

// Define types based on Better Auth
type User = typeof authClient.$Infer.Session.user;
type Session = typeof authClient.$Infer.Session.session;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  sendMagicLink: (email: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { data, isPending, error } = authClient.useSession();
  
  const user = data?.user || null;
  const session = data?.session || null;
  const loading = isPending;

  useEffect(() => {
    if (user) {
      Sentry.setUser({ 
        id: user.id, 
        email: user.email 
      });
      // Mark auth flow as active/complete in session manager
      sessionStorage.setItem('auth_flow_active', 'true');
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const signUp = async (email: string, password: string, name: string = "User") => {
    // DNS validation is now handled by Better Auth's `before:signUp` hook.
    // This client-side check is redundant but kept for immediate feedback if desired.
    const dnsValidation = await validateEmailDomainDNS(email);
    if (!dnsValidation.isValid) {
      trackFormSubmission("signup", false, { 
        reason: "invalid_dns",
        email_domain: email.split("@")[1] 
      });
      return { 
        error: { 
          message: dnsValidation.message || "This email domain cannot receive mail."
        } 
      };
    }

    const { data, error } = await authClient.signUp.email({
        email,
        password,
        name,
        callbackURL: `${window.location.origin}/`,
    });
    
    trackFormSubmission("signup", !error, { 
      email_domain: email.split("@")[1],
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
      const { data, error } = await authClient.signIn.email({
          email,
          password,
      });
      return { error };
  }

  const sendMagicLink = async (email: string) => {
    const { data, error } = await authClient.signIn.magicLink({
      email,
      callbackURL: `${window.location.origin}/verify-magic-link`,
    });
    return { error };
  };

  const signOut = async () => {
    trackUserAction("sign_out", "auth");
    
    // Notify other tabs immediately via SessionManager
    sessionManager.notifyUserLogout();
    
    await authClient.signOut();
    navigate(ROUTES.AUTH, { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, sendMagicLink, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
